import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material"
import { DateInput, DateTimeInput, TextInput, useTranslate } from "react-admin"
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { FilterList, HighlightOff } from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"

export const ItemsFilter = ({displayedFilters, setFilters, listFilters, setFilterValues}) => {
  const translate = useTranslate()
  
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()
  
  const showFilter = (filterName, value) => {
    setFilterValues((values) => ({
      ...values,
      [filterName]: value,
    }))
    setFilters((displayedCreditFilters) => ({
      ...displayedCreditFilters,
      [filterName]: true,
    }))
  }

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        startIcon={<FilterList />}
      >
        {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

export const PostFilterFormItems = ({ displayedFilters, filterValues,setFilters, setFilterValues })=>{
    const translate = useTranslate()
    const form = useForm()

    useEffect(()=>{
      let values_to_null = {...form.getValues()}
      for(let [key, _] of Object.entries(filterValues)){
          if(!displayedFilters[key]){
              if(key === 'due_date' || key === 'item__date_in' || key === 'created_at'){
                values_to_null[key+'__gte'] = null
                values_to_null[key+'__lte'] = null
                form.setValue(key+'__gte', null)
                form.setValue(key+'__lte', null)
              } else {
                values_to_null[key] = null
                form.setValue(key, null)
              }
          }
      }
      setFilterValues(values_to_null)
    },[displayedFilters])

    function convertToUTC(dateString) {
        return dateString
    }

    function convertFromUTC(dateString) {
        return dateString
    }

    const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null, displayedFilters = {}, setFilters }) => {
    const { setValue, getValues } = useFormContext()

    const onClickButton = () => {
      const copy = { ...displayedFilters }
      delete copy[prop]

      if (Array.isArray(fields) && fields.length > 0) {
        fields.forEach((field) => setValue(field, undefined))

        let values = getValues()
        if (values.business_unit__country_id__in) {
          values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
        }
        setFilters((filters)=>{return {...filters, [prop]:false}})
      }
    }

    return (
        <Box
          display={'flex'}
          alignItems={'flex-start'}
          marginRight={5}
          width={width}
          maxWidth={maxWidth}
          minWidth={minWidth}
        >
          <Box width={'34px'} paddingTop={1}>
            <IconButton size='small' onClick={onClickButton}>
            <HighlightOff />
            </IconButton>
          </Box>
          <Box width={'calc(100% - 34px)'} display={'flex'} height={'auto'}>
            {children}
          </Box>
        </Box>
    )
    }

    const onSubmit = () => {
      let filtersToSet = {...form.getValues()}
      if(displayedFilters?.due_date){
        filtersToSet['due_date__gte'] = filtersToSet['due_date__gte']?.split('T')[0]
        filtersToSet['due_date__lte'] = filtersToSet['due_date__lte']?.split('T')[0]
      }
      if(displayedFilters?.item__date_in){
        filtersToSet['item__date_in__gte'] = filtersToSet['item__date_in__gte']?.split('T')[0]
        filtersToSet['item__date_in__lte'] = filtersToSet['item__date_in__lte']?.split('T')[0]
      }
      if(displayedFilters?.item__date_out){
        filtersToSet['item__date_out__gte'] = filtersToSet['item__date_out__gte']?.split('T')[0]
        filtersToSet['item__date_out__lte'] = filtersToSet['item__date_out__lte']?.split('T')[0]
      }
      setFilterValues(filtersToSet)
    }

    if (!Object.values(displayedFilters ?? {}).some(elem => elem)) return null

    return(
      <FormProvider {...form}>
        <Box display='flex' alignItems='center'>
          <form style={{ flex: 1 }}>
            <Box display='flex' flexWrap='wrap'>
              {displayedFilters?.created_at && (
                <RemoveFilterField prop={'created_at'} fields={['created_at__gte', 'created_at__lte']} width={500} displayedFilters = {displayedFilters} setFilters={setFilters}>
                  <DateTimeInput
                    label={translate('ra.field.start_create_date')}
                    fullWidth
                    source='created_at__gte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                  <DateTimeInput
                    sx={{ ml: 5 }}
                    label={translate('ra.field.end_create_date')}
                    fullWidth
                    source='created_at__lte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                </RemoveFilterField>
              )}
              {displayedFilters?.due_date && (
                <RemoveFilterField prop={'due_date'} fields={['due_date__gte', 'due_date__lte']} width={500} displayedFilters = {displayedFilters} setFilters={setFilters}>
                  <DateInput
                    label={translate('ra.field.start_due_date')}
                    fullWidth
                    source='due_date__gte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                  <DateInput
                    sx={{ ml: 5 }}
                    label={translate('ra.field.end_due_date')}
                    fullWidth
                    source='due_date__lte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                </RemoveFilterField>
              )}
              {displayedFilters?.item__date_in && (
                <RemoveFilterField prop={'item__date_in'} fields={['item__date_in__gte', 'item__date_in__lte']} width={500} displayedFilters = {displayedFilters} setFilters={setFilters}>
                  <DateTimeInput
                    label={translate('ra.field.from')}
                    fullWidth
                    source='item__date_in__gte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                  <DateTimeInput
                    sx={{ ml: 5 }}
                    label={translate('ra.field.to')}
                    fullWidth
                    source='item__date_in__lte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                </RemoveFilterField>
              )}
              {displayedFilters?.item__date_out && (
                <RemoveFilterField prop={'item__date_out'} fields={['item__date_out__gte', 'item__date_out__lte']} width={500} displayedFilters = {displayedFilters} setFilters={setFilters}>
                  <DateTimeInput
                    label={translate('ra.field.from')}
                    fullWidth
                    source='item__date_out__gte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                  <DateTimeInput
                    sx={{ ml: 5 }}
                    label={translate('ra.field.to')}
                    fullWidth
                    source='item__date_out__lte'
                    format={convertFromUTC}
                    parse={convertToUTC}
                    helperText=''
                  />
                </RemoveFilterField>
              )}
              {displayedFilters?.code__in && (
                  <RemoveFilterField displayedFilters = {displayedFilters} setFilters={setFilters} prop={'code__in'} fields={['code__in']} width={300}>
                    <TextInput
                        label={translate('ra.field.code')}
                        fullWidth
                        source='code__in'
                    />
                  </RemoveFilterField>
              )}
              {displayedFilters?.billing_ilike__in && (
                  <RemoveFilterField displayedFilters = {displayedFilters} setFilters={setFilters} prop={'billing_ilike__in'} fields={['billing_ilike__in']} width={300}>
                    <TextInput
                        label={translate('ra.field.billing')}
                        fullWidth
                        source='billing_ilike__in'
                    />
                  </RemoveFilterField>
              )}
              {displayedFilters?.reservation__number__in && (
                  <RemoveFilterField displayedFilters = {displayedFilters} setFilters={setFilters} prop={'reservation__number__in'} fields={['reservation__number__in']} width={300}>
                    <TextInput
                        label={translate('ra.field.reserve')}
                        fullWidth
                        source='reservation__number__in'
                    />
                  </RemoveFilterField>
              )}
              {displayedFilters?.status__in && (
                  <RemoveFilterField displayedFilters = {displayedFilters} setFilters={setFilters} prop={'status__in'} fields={['status__in']} width={300}>
                    <TextInput
                        label={translate('ra.field.status')}
                        fullWidth
                        source='status__in'
                    />
                  </RemoveFilterField>
              )}
            </Box>
          </form>

          <Box>
            <LoadingButton variant='outlined' onClick={onSubmit}>
              {translate('ra.action.filter')}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    )
}